import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { Eye, EyeOff, Lock, Mail, Shield, Loader2, ShieldCheck } from 'lucide-react';
import { useAuthStore } from '@/store/authStore';
import api from '@/services/api';

const AdminLogin = () => {
    const navigate = useNavigate();
    const { setAuth } = useAuthStore();
    const [showPassword, setShowPassword] = useState(false);
    const [formData, setFormData] = useState({
        email: '',
        password: ''
    });
    const [twoFactorData, setTwoFactorData] = useState({
        code: ''
    });
    const [showTwoFactor, setShowTwoFactor] = useState(false);
    const [errors, setErrors] = useState({});

    // Admin login mutation
    const loginMutation = useMutation({
        mutationFn: async (data) => {
            const response = await api.post('/auth/admin/login', data);
            return response.data;
        },
        onSuccess: (data) => {
            if (data.requiresTwoFactor) {
                setShowTwoFactor(true);
                setErrors({});
            } else {
                handleLoginSuccess(data);
            }
        },
        onError: (error) => {
            const message = error.response?.data?.message || 'Invalid credentials or insufficient permissions';
            setErrors({ general: message });
        }
    });

    // 2FA verification mutation
    const verifyTwoFactorMutation = useMutation({
        mutationFn: async (data) => {
            // Include the 2FA token in the original login request
            const response = await api.post('/auth/admin/login', {
                email: formData.email,
                password: formData.password,
                totpToken: data.code
            });
            return response.data;
        },
        onSuccess: (data) => {
            handleLoginSuccess(data);
        },
        onError: (error) => {
            const message = error.response?.data?.message || 'Invalid verification code';
            setErrors({ twoFactor: message });
        }
    });

    const handleLoginSuccess = (data) => {
        setAuth({ token: data.token, admin: data.admin, userType: "admin" }); // true flag for admin
        navigate('/admin/dashboard');
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        // Clear errors when user types
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    const handleTwoFactorChange = (e) => {
        const { value } = e.target;
        setTwoFactorData(prev => ({ ...prev, code: value }));
        if (errors.twoFactor) {
            setErrors(prev => ({ ...prev, twoFactor: '' }));
        }
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.email) {
            newErrors.email = 'Email is required';
        } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
            newErrors.email = 'Email is invalid';
        }

        if (!formData.password) {
            newErrors.password = 'Password is required';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (validateForm()) {
            loginMutation.mutate(formData);
        }
    };

    const handleTwoFactorSubmit = (e) => {
        e.preventDefault();
        if (!twoFactorData.code) {
            setErrors({ twoFactor: 'Verification code is required' });
            return;
        }
        verifyTwoFactorMutation.mutate({ code: twoFactorData.code });
    };

    if (showTwoFactor) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-900 py-12 px-4 sm:px-6 lg:px-8">
                <div className="max-w-md w-full space-y-8">
                    <div>
                        <div className="flex justify-center">
                            <div className="bg-yellow-500 p-3 rounded-full">
                                <Shield className="h-12 w-12 text-gray-900" />
                            </div>
                        </div>
                        <h2 className="mt-6 text-center text-3xl font-extrabold text-white">
                            Admin Two-Factor Authentication
                        </h2>
                        <p className="mt-2 text-center text-sm text-gray-400">
                            Enter the verification code from your authenticator app
                        </p>
                    </div>

                    <form className="mt-8 space-y-6" onSubmit={handleTwoFactorSubmit}>
                        <div>
                            <label htmlFor="code" className="block text-sm font-medium text-gray-300 mb-1">
                                Verification Code
                            </label>
                            <input
                                id="code"
                                name="code"
                                type="text"
                                autoComplete="one-time-code"
                                required
                                maxLength="6"
                                className={`appearance-none relative block w-full px-3 py-2 border ${
                                    errors.twoFactor ? 'border-red-500' : 'border-gray-600'
                                } placeholder-gray-500 text-white bg-gray-800 rounded-md focus:outline-none focus:ring-yellow-500 focus:border-yellow-500 focus:z-10 sm:text-sm`}
                                placeholder="Enter 6-digit code"
                                value={twoFactorData.code}
                                onChange={handleTwoFactorChange}
                                autoFocus
                            />
                            {errors.twoFactor && (
                                <p className="mt-1 text-sm text-red-400">{errors.twoFactor}</p>
                            )}
                        </div>

                        <div>
                            <button
                                type="submit"
                                disabled={verifyTwoFactorMutation.isPending}
                                className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-gray-900 bg-yellow-500 hover:bg-yellow-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500 focus:ring-offset-gray-900 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {verifyTwoFactorMutation.isPending ? (
                                    <>
                                        <Loader2 className="animate-spin h-5 w-5 mr-2" />
                                        Verifying...
                                    </>
                                ) : (
                                    'Verify'
                                )}
                            </button>
                        </div>

                        <div className="text-center">
                            <button
                                type="button"
                                onClick={() => {
                                    setShowTwoFactor(false);
                                    setTwoFactorData({ code: '' });
                                    setErrors({});
                                }}
                                className="text-sm text-yellow-500 hover:text-yellow-400"
                            >
                                Back to login
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen flex items-center justify-center bg-gray-900 py-12 px-4 sm:px-6 lg:px-8">
            <div className="max-w-md w-full space-y-8">
                <div>
                    <div className="flex justify-center">
                        <div className="bg-yellow-500 p-3 rounded-full">
                            <ShieldCheck className="h-12 w-12 text-gray-900" />
                        </div>
                    </div>
                    <h2 className="mt-6 text-center text-3xl font-extrabold text-white">
                        Admin Portal
                    </h2>
                    <p className="mt-2 text-center text-sm text-gray-400">
                        Sign in with your administrator credentials
                    </p>
                    <p className="mt-2 text-center text-sm text-gray-500">
                        <Link to="/login" className="font-medium text-yellow-500 hover:text-yellow-400">
                            Not an admin? Sign in as user
                        </Link>
                    </p>
                </div>

                <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
                    {errors.general && (
                        <div className="rounded-md bg-red-900/50 border border-red-700 p-4">
                            <p className="text-sm text-red-200">{errors.general}</p>
                        </div>
                    )}

                    <div className="rounded-md shadow-sm -space-y-px">
                        <div>
                            <label htmlFor="email-address" className="sr-only">
                                Email address
                            </label>
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <Mail className={`h-5 w-5 ${errors.email ? 'text-red-400' : 'text-gray-500'}`} />
                                </div>
                                <input
                                    id="email-address"
                                    name="email"
                                    type="email"
                                    autoComplete="email"
                                    required
                                    className={`appearance-none rounded-none relative block w-full pl-10 px-3 py-2 border ${
                                        errors.email ? 'border-red-500' : 'border-gray-600'
                                    } placeholder-gray-500 text-white bg-gray-800 rounded-t-md focus:outline-none focus:ring-yellow-500 focus:border-yellow-500 focus:z-10 sm:text-sm`}
                                    placeholder="Admin email address"
                                    value={formData.email}
                                    onChange={handleChange}
                                />
                            </div>
                            {errors.email && (
                                <p className="mt-1 text-sm text-red-400">{errors.email}</p>
                            )}
                        </div>
                        <div>
                            <label htmlFor="password" className="sr-only">
                                Password
                            </label>
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <Lock className={`h-5 w-5 ${errors.password ? 'text-red-400' : 'text-gray-500'}`} />
                                </div>
                                <input
                                    id="password"
                                    name="password"
                                    type={showPassword ? 'text' : 'password'}
                                    autoComplete="current-password"
                                    required
                                    className={`appearance-none rounded-none relative block w-full pl-10 pr-10 px-3 py-2 border ${
                                        errors.password ? 'border-red-500' : 'border-gray-600'
                                    } placeholder-gray-500 text-white bg-gray-800 rounded-b-md focus:outline-none focus:ring-yellow-500 focus:border-yellow-500 focus:z-10 sm:text-sm`}
                                    placeholder="Password"
                                    value={formData.password}
                                    onChange={handleChange}
                                />
                                <button
                                    type="button"
                                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                                    onClick={() => setShowPassword(!showPassword)}
                                >
                                    {showPassword ? (
                                        <EyeOff className="h-5 w-5 text-gray-500 hover:text-gray-400" />
                                    ) : (
                                        <Eye className="h-5 w-5 text-gray-500 hover:text-gray-400" />
                                    )}
                                </button>
                            </div>
                            {errors.password && (
                                <p className="mt-1 text-sm text-red-400">{errors.password}</p>
                            )}
                        </div>
                    </div>

                    <div className="flex items-center justify-between">
                        <div className="text-sm">
                            <Link to="/forgot-password" className="font-medium text-yellow-500 hover:text-yellow-400">
                                Forgot your password?
                            </Link>
                        </div>
                    </div>

                    <div>
                        <button
                            type="submit"
                            disabled={loginMutation.isPending}
                            className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-gray-900 bg-yellow-500 hover:bg-yellow-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500 focus:ring-offset-gray-900 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                            {loginMutation.isPending ? (
                                <>
                                    <Loader2 className="animate-spin h-5 w-5 mr-2" />
                                    Authenticating...
                                </>
                            ) : (
                                <>
                  <span className="absolute left-0 inset-y-0 flex items-center pl-3">
                    <ShieldCheck className="h-5 w-5 text-gray-900" />
                  </span>
                                    Sign in as Admin
                                </>
                            )}
                        </button>
                    </div>

                    <div className="mt-4 text-center">
                        <p className="text-xs text-gray-500">
                            This is a secure area. All login attempts are logged.
                        </p>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default AdminLogin;